import { useState, useCallback, memo } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { GitHubSignInButton } from '../common/GitHubSignInButton';
import { Card } from '@/components/ui/card';
import { AnimatePresence } from 'framer-motion';
import { motion } from '@/components/ui/motion-provider';
import { useMediaQuery } from "@/hooks/use-media-query";
import { ChevronDown, Search, Github, Zap, MapPin, FileDown, Sparkles } from 'lucide-react';
import { cn } from "@/lib/utils";

interface FAQItem {
  question: string;
  answer: string;
  teaser: string;
  icon: React.ReactNode;
}

const faqItems: FAQItem[] = [
  {
    question: "What is DevFinder?",
    teaser: "Your AI-powered gateway to finding exceptional tech talent on GitHub",
    answer: "Discover DevFinder—your friendly gateway to exceptional tech talent on GitHub. Our platform makes it simple and fun to search for the perfect developer. With real-time data, smart filters, and a user-centric design, you can easily find front-end maestros, AI innovators, or full-stack experts who are just the right fit for your team.",
    icon: <Search className="w-5 h-5" />
  },
  {
    question: "Do I need to sign in?",
    teaser: "Quick GitHub sign-in unlocks all features instantly",
    answer: "Yes, and it's a breeze! Just sign in with your GitHub account to unlock all of DevFinder's awesome features. This quick and secure process lets you enjoy personalized tools like saved searches and custom lists, ensuring a seamless experience without any extra hassle.",
    icon: <Github className="w-5 h-5" />
  },
  {
    question: "Is DevFinder free to use?",
    teaser: "Free access to powerful search tools and exports",
    answer: "Absolutely! DevFinder is completely free to use. Enjoy unlimited searches, powerful filters, and easy CSV exports at no cost. While we're planning to introduce premium features for those extra supercharged needs, our essential search tools will always be free—because connecting great talent with opportunity should be accessible to everyone.",
    icon: <Zap className="w-5 h-5" />
  },
  {
    question: "How accurate are the search results?",
    teaser: "Real-time data from GitHub's API with smart filtering",
    answer: "Our search results are highly accurate and designed with your success in mind. By directly integrating with GitHub's API and using a smart algorithm that takes into account recent activity, contribution frequency, and more, DevFinder delivers the most current and relevant developer profiles. Plus, our intuitive filters let you refine your results to perfectly match your criteria.",
    icon: <Sparkles className="w-5 h-5" />
  },
  {
    question: "Can I export the search results?",
    teaser: "Easy CSV exports with comprehensive developer data",
    answer: "Absolutely! Once you're signed in, you can easily export your search results in CSV format. This export includes all the essential details—from GitHub usernames and full names to emails (when available), locations, and contribution stats—making it simple to integrate DevFinder into your recruitment workflow.",
    icon: <FileDown className="w-5 h-5" />
  },
  {
    question: "How is the geographical data collected?",
    teaser: "Transparent location data from GitHub and OpenStreetMap",
    answer: "We collect geographical data in a transparent and reliable way. By leveraging GitHub's public events API along with OpenStreetMap's Nominatim service, we convert user-provided profile locations into accurate, real-time data. This ensures you're always seeing the most current location information for each developer.",
    icon: <MapPin className="w-5 h-5" />
  }
];

// Memoized FAQ item component for better performance
const FAQItem = memo(({ 
  item, 
  index, 
  isOpen, 
  isMobile, 
  onToggle 
}: { 
  item: FAQItem; 
  index: number; 
  isOpen: boolean; 
  isMobile: boolean;
  onToggle: (index: number) => void;
}) => (
  <motion.div
    initial={false}
    animate={{ 
      backgroundColor: isOpen ? 'hsl(var(--muted))' : 'transparent',
      scale: isOpen ? 1.02 : 1
    }}
    className={cn(
      "rounded-xl border border-border/50 overflow-hidden backdrop-blur-sm",
      isOpen ? "shadow-lg ring-1 ring-primary/5" : "hover:border-border",
      "transition-all duration-200"
    )}
  >
    <button
      onClick={() => onToggle(index)}
      className={cn(
        "w-full px-6 py-5 text-left",
        "hover:bg-muted/50 transition-colors duration-200",
        isMobile ? "text-base" : "text-lg"
      )}
      aria-expanded={isOpen}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2 rounded-lg transition-colors duration-200",
            isOpen ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          )}>
            {item.icon}
          </div>
          <span className="font-semibold">{item.question}</span>
        </div>
        <ChevronDown 
          className={cn(
            "flex-shrink-0 w-5 h-5 transition-transform duration-200",
            isOpen ? "rotate-180 text-primary" : "text-muted-foreground"
          )}
        />
      </div>
      {!isOpen && (
        <p className="mt-2 text-sm text-muted-foreground/80 pl-12">
          {item.teaser}
        </p>
      )}
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className={cn(
            "px-6 pb-5 whitespace-pre-line",
            isMobile ? "text-sm leading-relaxed" : "text-base leading-relaxed",
            "text-muted-foreground pl-12"
          )}>
            {item.answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
));

FAQItem.displayName = 'FAQItem';

export const FAQ = memo(function FAQ() {
  const { user } = useAuth();
  const [openItems, setOpenItems] = useState<number[]>([0]); // First item open by default
  const isMobile = useMediaQuery("(max-width: 768px)");

  const toggleItem = useCallback((index: number) => {
    setOpenItems(prev => 
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [index] // Only one item open at a time
    );
  }, []);

  return (
    <Card className="w-full bg-background/80 relative overflow-hidden border-0 rounded-[2rem]">
      {/* Enhanced gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))]" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16 sm:py-20">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="font-poppins text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 sm:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/80">
            Frequently Asked Questions
          </h2>
          <p className="font-inter text-muted-foreground/90 text-lg sm:text-xl max-w-2xl mx-auto">
            Everything you need to know about finding your next developer
          </p>
        </motion.div>

        <motion.div 
          className="space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {faqItems.map((item, index) => (
            <FAQItem
              key={index}
              item={item}
              index={index}
              isOpen={openItems.includes(index)}
              isMobile={isMobile}
              onToggle={toggleItem}
            />
          ))}
        </motion.div>

        {!user && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 sm:mt-20 flex flex-col items-center justify-center border-t border-border/50 pt-16"
          >
            <div className="text-center max-w-xl mx-auto">
              <h3 className="font-poppins text-2xl sm:text-3xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/80">
                Ready to Get Started?
              </h3>
              <p className="font-inter text-lg sm:text-xl text-muted-foreground mb-8">
                Join hundreds of recruiters who are already using DevFinder to make meaningful connections.
              </p>
              <GitHubSignInButton 
                variant="faq"
                size="lg"
                className="font-poppins bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200 hover:translate-y-[-1px]"
              />
            </div>
          </motion.div>
        )}
      </div>
    </Card>
  );
});
